import { useCallback, useContext, useState } from 'react';
import usePageMeta from '../hooks/usePageMeta';
import { AuthContext } from '../context/AuthContext';
import { submitSprite } from '../services/sprites';
import PixelCanvas from '../components/sprite-editor/PixelCanvas';
import Palette from '../components/sprite-editor/Palette';
import FrameStrip from '../components/sprite-editor/FrameStrip';
import AnimationPreview from '../components/sprite-editor/AnimationPreview';
import {
  COLS,
  ROW_LABELS,
  COLUMN_LABELS,
  DEFAULT_PALETTE,
  emptyFrame,
  emptySheet,
  floodFill,
  framesToDataUrl,
  idx,
  loadSheet,
  SHEET_H,
  SHEET_W,
} from '../components/sprite-editor/spriteData';

const HERO_SHEET = `${process.env.PUBLIC_URL || ''}/sprites/hero-slim-short.png`;

/**
 * Pixel art editor.
 *
 * Deliberately not a replacement for Aseprite. It exists to (a) let generated
 * placeholder art be opened and adjusted, and (b) produce a correctly-shaped
 * sheet to hand to Aseprite for real work. The export is a plain 128x128 PNG
 * matching docs/sprite-spec.md, so the round trip is lossless.
 */
function SpriteEditor() {
  const { token } = useContext(AuthContext);
  const [frames, setFrames] = useState(emptySheet);
  const [active, setActive] = useState(0);
  const [color, setColor] = useState(DEFAULT_PALETTE[0]);
  const [tool, setTool] = useState('pencil');
  const [showGrid, setShowGrid] = useState(true);
  const [status, setStatus] = useState(null);
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  usePageMeta({ title: 'Sprite editor', canonicalPath: '/sprites' });

  const updateActive = useCallback((mutate) => {
    setFrames((prev) => prev.map((f, i) => (i === active ? mutate(f) : f)));
  }, [active]);

  const handlePaint = useCallback((x, y, currentTool) => {
    updateActive((pixels) => {
      if (currentTool === 'fill') {
        return floodFill(pixels, x, y, color);
      }
      const value = currentTool === 'eraser' ? null : color;
      if (pixels[idx(x, y)] === value) return pixels; // no-op, skip the copy
      const next = pixels.slice();
      next[idx(x, y)] = value;
      return next;
    });
  }, [color, updateActive]);

  const handlePick = useCallback((picked) => {
    if (picked) setColor(picked);
    setTool('pencil');
  }, []);

  const handleLoadPlaceholder = async () => {
    try {
      setFrames(await loadSheet(HERO_SHEET));
      setStatus({ kind: 'ok', text: 'Loaded hero-slim-short.png' });
    } catch (err) {
      setStatus({ kind: 'error', text: err.message });
    }
  };

  const handleExport = () => {
    const link = document.createElement('a');
    link.href = framesToDataUrl(frames);
    link.download = 'hero-slim-short.png';
    link.click();
    setStatus({ kind: 'ok', text: `Exported ${SHEET_W}x${SHEET_H} — replace client/public/sprites/hero-slim-short.png` });
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await submitSprite(token, { png: framesToDataUrl(frames), note });
      setNote('');
      setStatus({ kind: 'ok', text: 'Submitted for review. You will see it in your submissions list.' });
    } catch (err) {
      // Surfaces real server rejections — wrong size, not a PNG, no access,
      // rate limited. The server is the authority, so its message is the
      // useful one.
      setStatus({ kind: 'error', text: err.message || 'Submission failed.' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleExportJson = () => {
    const meta = {
      frameSize: SHEET_W / COLS,
      columns: COLS,
      rows: ROW_LABELS.length,
      rowOrder: ROW_LABELS,
      columnMeaning: COLUMN_LABELS,
    };
    const blob = new Blob([JSON.stringify(meta, null, 2)], { type: 'application/json' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'hero.json';
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const activeRow = Math.floor(active / COLS);
  const activeCol = active % COLS;

  return (
    <div className="min-h-screen bg-gray-950 py-8">
      <div className="mx-auto max-w-6xl px-6">
        <header className="mb-6">
          <h1 className="text-2xl font-bold text-white">Sprite editor</h1>
          <p className="text-sm text-gray-400 mt-1">
            Rough out a
            {` ${SHEET_W}x${SHEET_H} `}
            sheet, then refine it in Aseprite. Export overwrites
            {' '}
            <code className="text-gray-300">client/public/sprites/hero-slim-short.png</code>
            .
          </p>
        </header>

        {status && (
          <div
            className={`mb-4 rounded-md px-4 py-2 text-sm ${
              status.kind === 'error'
                ? 'bg-red-900/40 text-red-300'
                : 'bg-emerald-900/40 text-emerald-300'
            }`}
          >
            {status.text}
          </div>
        )}

        <div className="grid gap-6 lg:grid-cols-[200px_auto_240px]">
          <aside className="space-y-6">
            <Palette
              color={color}
              onColor={setColor}
              tool={tool}
              onTool={setTool}
              showGrid={showGrid}
              onToggleGrid={() => setShowGrid((g) => !g)}
            />

            <div className="space-y-2">
              <button
                type="button"
                onClick={handleLoadPlaceholder}
                className="w-full px-3 py-2 text-xs rounded bg-gray-800 text-gray-200 hover:bg-gray-700"
              >
                Load hero-slim-short.png
              </button>
              <button
                type="button"
                onClick={handleExport}
                className="w-full px-3 py-2 text-xs rounded bg-gray-800 text-gray-200 hover:bg-gray-700"
              >
                Export PNG
              </button>
              <button
                type="button"
                onClick={handleExportJson}
                className="w-full px-3 py-2 text-xs rounded bg-gray-800 text-gray-200 hover:bg-gray-700"
              >
                Export JSON
              </button>
            </div>

            <div className="space-y-2 pt-2 border-t border-gray-800">
              <label htmlFor="submit-note" className="block">
                <span className="block text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">
                  What changed?
                </span>
                <textarea
                  id="submit-note"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={3}
                  maxLength={1000}
                  placeholder="Shorter idle pose, warmer skin tone…"
                  className="w-full rounded bg-gray-800 border border-gray-700 text-xs text-gray-200 p-2 placeholder-gray-600"
                />
              </label>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={submitting}
                className="w-full px-3 py-2 text-xs font-semibold rounded bg-primary text-navy hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? 'Submitting…' : 'Submit for review'}
              </button>
              <p className="text-[10px] text-gray-500">
                An admin reviews every submission before it reaches the game.
              </p>
            </div>
          </aside>

          <main className="space-y-3">
            <div className="text-xs text-gray-400">
              Editing
              {' '}
              <strong className="text-gray-200">
                {ROW_LABELS[activeRow]}
                {' · '}
                {COLUMN_LABELS[activeCol]}
              </strong>
            </div>

            <PixelCanvas
              pixels={frames[active]}
              color={color}
              tool={tool}
              showGrid={showGrid}
              onPaint={handlePaint}
              onPick={handlePick}
            />

            <p className="text-[10px] text-gray-500 max-w-md">
              The gold lines mark the horizontal centre and the foot line
              (y=28). Characters should be centred and stand on that line, or
              they will bob relative to each other in game.
            </p>
          </main>

          <aside className="space-y-6">
            <AnimationPreview frames={frames} row={activeRow} />
            <FrameStrip
              frames={frames}
              active={active}
              onSelect={setActive}
              onClear={() => updateActive(() => emptyFrame())}
            />
          </aside>
        </div>
      </div>
    </div>
  );
}

export default SpriteEditor;
